import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { Switch, Tooltip } from 'antd';
import { scMixinPseudoBlock } from '@utils/emotion/mixins';
import Draggable, { DraggableEvent, DraggableData, DraggableBounds } from 'react-draggable';
import {
  formatTimeToDigit,
  formatDigitToTimeFrame,
  getTimeFormat,
  formatDigitToHHmmss,
} from '~/utils';
import { MIN_DURATION_SEC, calcBeginBoundTimeLimit, calcEndBoundTimeLimit } from '~/utils/chapter';
import TimeInput from '@components/TimeInput';

import useStores from '@lib/hooks/useStores';
import { IVideoStore } from '@stores/videoStore';
import { IChapter } from '@interfaces/pose.interface';
import { HoveredChapter } from '@interfaces/video.interface';
import { useTranslation } from 'react-i18next';
// import { Callback } from 'i18next';

import { ReactComponent as IconVideo } from '@svg/icon-video.svg';
import { ReactComponent as IconLibrary } from '@svg/icon-library-add.svg';
import { ReactComponent as IconVector } from '@svg/icon-vector.svg';
import { ReactComponent as IconMagnetDis } from '@svg/icon-magnet-disabled.svg';

interface VideoTimelineProps {
  type: 'coords' | 'chapter';
  editable: boolean;
  duration: number;
  currentTime: number;
  currentProgress: number;
  currentChapterIdx?: number;
  currentSecFrameIdx?: number;
  chapterData: IChapter[];
  updateChapterData?: React.Dispatch<React.SetStateAction<IChapter[]>>;
  closestTime?: number;
  hoveredChapter: HoveredChapter;
  onChapterMouseOver: (data: HoveredChapter) => void;
  clickedChapter?: HoveredChapter;
  onChapterClick?: (data: HoveredChapter) => void;
}

const transcodingFrameRate = parseFloat(process.env.REACT_APP_TRANSCODING_FRAME_RATE!);

const VideoTimeline: React.FC<VideoTimelineProps> = ({
  type,
  editable,
  duration,
  currentTime,
  currentProgress,
  currentChapterIdx,
  currentSecFrameIdx,
  chapterData,
  updateChapterData,
  closestTime,
  hoveredChapter,
  onChapterMouseOver,
  clickedChapter,
  onChapterClick,
}) => {
  const { t } = useTranslation();
  const { videoStore } = useStores<{ videoStore: IVideoStore }>();
  const timelineRootElement = useRef<HTMLDivElement | null>(null);
  const [isDOMLoaded, setDOMLoaded] = useState(false); // 돔 Load 여부
  const [timestamp, setTimestamp] = useState('');
  const [timelineDimensionX, setTimelineDimensionX] = useState(0); // 타임라인 Width
  const [isPausedBeforePlayheadDrag, setPausedBeforePlayheadDrag] = useState(true); // 플레이헤드 드래그시 플레이어 재생/정지 여부 저장
  const [isPlayheadHoverActive, setPlayheadHoverActive] = useState(false); // 플레이헤드 호버 활성 여부
  const [isPlayheadHoverMouseDown, setPlayheadHoverMouseDown] = useState(false); // 플레이헤드 호버 마우스다운 여부
  const [playheadHoverPosX, setPlayheadHoverPosX] = useState(0); // 플레이헤드 clientX
  const [isOnMagnet, setOnMagnet] = useState(true); // snap 기능 여부
  const [snapPosition, setSnapPosition] = useState({
    type: '',
    x: -1,
  });
  const [dragChapterIdx, setDragChapterIdx] = useState<number | null>(null);
  const [playheadBounds, setPlayheadBounds] = useState<DraggableBounds>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  const [connSeekbar, setConnSeekbar] = useState(false);
  const [isChapterClick, setIsChapterClick] = useState(false);

  const timeToOffsetX = useCallback(
    (time: number) => (time / duration) * timelineDimensionX,
    [duration, timelineDimensionX],
  );

  const offsetXToTime = useCallback(
    (offsetX: number) => (offsetX / timelineDimensionX) * duration,
    [timelineDimensionX, duration],
  );

  useEffect(() => {
    if (timelineRootElement.current) {
      setTimelineDimensionX(timelineRootElement.current.clientWidth);
      setDOMLoaded(true);
    }

    const resizeHandler = () => {
      if (timelineRootElement.current) {
        setTimelineDimensionX(timelineRootElement.current.clientWidth);

        window.setTimeout(() => {
          if (
            timelineRootElement.current &&
            timelineDimensionX !== timelineRootElement.current.clientWidth
          ) {
            setTimelineDimensionX(timelineRootElement.current.clientWidth);
          }
        }, 300);
      }
    };

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [duration, timelineDimensionX]);

  useEffect(() => {
    const t = formatDigitToTimeFrame(currentTime, transcodingFrameRate, getTimeFormat(duration));

    switch (type) {
      case 'chapter':
        setTimestamp(t);
        break;
      case 'coords':
        const beforeFrameIndex = Number(timestamp.split(':')[3]);
        if (currentSecFrameIdx !== beforeFrameIndex) {
          setTimestamp(`${t}:${currentSecFrameIdx}`);
        }
        break;
      default:
    }
  }, [timestamp, currentTime, currentSecFrameIdx, type, duration]);

  useEffect(() => {
    const { playerRef } = videoStore;
    if (type !== 'coords' || !playerRef) return;

    const c = chapterData.find((data) => data.chapterIdx === currentChapterIdx);
    if (!c) return;

    const { startTime, endTime } = c;
    const boundStart = timeToOffsetX(startTime);
    const boundEnd = timeToOffsetX(endTime);

    playerRef.pause();
    playerRef.currentTime(startTime);

    setPlayheadBounds({
      top: 0,
      right: boundEnd,
      bottom: 0,
      left: boundStart,
    });
  }, [type, chapterData, currentChapterIdx, videoStore, timeToOffsetX]);

  const PlayheadStartEventHandler = useCallback(() => {
    const { playerRef } = videoStore;
    if (!playerRef) return;

    setPlayheadHoverActive(false);
    setPausedBeforePlayheadDrag(playerRef.paused());
    playerRef.pause();
  }, [videoStore]);

  const PlayheadDragEventHandler = useCallback(
    (e: DraggableEvent, data: DraggableData) => {
      e.preventDefault();
      e.stopPropagation();

      const { playerRef } = videoStore;
      if (!playerRef) return;

      setPlayheadHoverActive(false);
      const prevTime = playerRef.currentTime();
      const prevPositionX = timeToOffsetX(prevTime);
      const nextPositionX = prevPositionX + data.deltaX;
      const nextTime = offsetXToTime(nextPositionX);

      playerRef.currentTime(nextTime);
    },
    [videoStore, timeToOffsetX, offsetXToTime],
  );

  const PlayheadStopEventHandler = useCallback(() => {
    const { playerRef } = videoStore;
    if (!playerRef) return;
    setPlayheadHoverActive(false);
    if (!isPausedBeforePlayheadDrag) playerRef.play();
  }, [videoStore, isPausedBeforePlayheadDrag]);

  const PlayheadBackgroundEnterEventHandler = () => setPlayheadHoverActive(true);
  const PlayheadBackgroundLeaveEventHandler = () => {
    onChapterMouseOver({ idx: -1, from: null });
    setPlayheadHoverActive(false);
    setPlayheadHoverMouseDown(false);
  };

  const getHoverChapterIndex = useCallback(
    (hoverPosX: number, type: 'hover' | 'click') => {
      for (let i = 0; i < chapterData.length; i++) {
        const chapter = chapterData[i];

        if (
          chapter.startTime <= offsetXToTime(hoverPosX) &&
          chapter.endTime >= offsetXToTime(hoverPosX)
        ) {
          if (type === 'hover') onChapterMouseOver({ idx: i, from: 'timeline' });
          else onChapterClick!({ idx: i, from: 'timeline' });
          return;
        }
      }
      if (type === 'hover') onChapterMouseOver({ idx: -1, from: 'timeline' });
      else onChapterClick!({ idx: -1, from: 'timeline' });
    },
    [onChapterMouseOver, onChapterClick, chapterData, offsetXToTime],
  );

  const PlayheadBackgroundDownEventHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      const timelineOffsetLeft = e.currentTarget.getBoundingClientRect().x;
      const hoverPosX = e.clientX - timelineOffsetLeft;

      if (videoStore.playerRef) {
        if (type === 'coords') {
          if (hoverPosX < playheadBounds.right! && hoverPosX > playheadBounds.left!) {
            setPlayheadHoverMouseDown(true);
            videoStore.playerRef.currentTime(offsetXToTime(hoverPosX));
          }
        } else {
          setPlayheadHoverMouseDown(true);
          videoStore.playerRef.currentTime(offsetXToTime(hoverPosX));

          getHoverChapterIndex(hoverPosX, 'click');
        }
      }
    },
    [
      videoStore.playerRef,
      type,
      playheadBounds.right,
      playheadBounds.left,
      offsetXToTime,
      getHoverChapterIndex,
    ],
  );

  const PlayheadBackgroundMoveEventHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      const { playerRef } = videoStore;
      const timelineOffsetLeft = e.currentTarget.getBoundingClientRect().x;
      const hoverPosX = e.clientX - timelineOffsetLeft;
      const currentTime = offsetXToTime(hoverPosX);

      if (playerRef && isPlayheadHoverMouseDown) {
        if (type === 'coords') {
          if (hoverPosX < playheadBounds.right! && hoverPosX > playheadBounds.left!) {
            playerRef.currentTime(currentTime);
          } else {
            setPlayheadHoverMouseDown(false);
          }
        } else {
          playerRef.currentTime(currentTime);
        }
      } else {
        setPlayheadHoverMouseDown(false);
        setPlayheadHoverPosX(hoverPosX);

        getHoverChapterIndex(hoverPosX, 'hover');
      }
    },
    [
      videoStore,
      offsetXToTime,
      isPlayheadHoverMouseDown,
      type,
      playheadBounds.right,
      playheadBounds.left,
      getHoverChapterIndex,
    ],
  );

  const PlayheadBackgroundUpEventHandler = useCallback(() => {
    if (type === 'coords' && isPlayheadHoverMouseDown) {
      if (videoStore.playerRef) {
        videoStore.playerRef.currentTime(Number(closestTime));
      }

      // const currentSec = Math.floor(currentTime);
      // const currentChapterSecKeys = Object.keys(groupedFrameData.current);

      // if (currentChapterSecKeys.includes(currentSec.toString())) {
      //   const currentSecFrameTimeKeys = Object.keys(groupedFrameData.current[currentSec]);
      //   const closestTime = currentSecFrameTimeKeys.reduce((p: any, c: any, cIndex: any) =>
      //     Math.abs(c - currentTime) < Math.abs(p - currentTime) ? c : p,
      //   );

      //   if (videoStore.playerRef) {
      //     videoStore.playerRef.currentTime = Number(closestTime);
      //   }
      // }
    }

    setPlayheadHoverMouseDown(false);
  }, [type, closestTime, isPlayheadHoverMouseDown, videoStore.playerRef]);

  // useEffect(() => {
  //   if(timelineRootElement.current) {
  //     timelineRootElement.current.addEventListener('mouseout', PlayheadBackgroundUpEventHandler);
  //   }
  // }, [timelineRootElement]);

  const isNearPlayHead = useCallback(
    (boundary: number) => Math.abs(timeToOffsetX(currentTime) - boundary) <= 20,
    [currentTime, timeToOffsetX],
  );

  const isValidRange = useCallback(
    (_currentIdx: string, newStartTime: number, newEndTime: number) => {
      const currentIdx = +_currentIdx;
      if (newStartTime < 0 || duration < newEndTime) return false;
      // 다른 챕터의 사이에 해당 챕터의 시작 및 종료 값이 포함되는지 체크
      return !chapterData.find((element, idx) => {
        return (
          currentIdx !== idx &&
          ((element.startTime <= newStartTime && newStartTime <= element.endTime) ||
            (element.startTime <= newEndTime && newEndTime <= element.endTime))
        );
      });
    },
    [chapterData, duration],
  );

  const ChapterBoundaryDragEventHandler = useCallback(
    (e: DraggableEvent, dragData: DraggableData) => {
      const { x: curBoundaryOffsetX, node } = dragData;
      const { boundary, index } = node.dataset;
      if (!boundary || !index) return;

      if (connSeekbar) {
        PlayheadDragEventHandler(e, dragData);
      }

      switch (e.type) {
        case 'mousemove':
          if (index) setDragChapterIdx(+index);
          break;
        case 'mouseup':
          setDragChapterIdx(null);
          setSnapPosition({ type: '', x: -1 });
          return;
        default:
      }

      let newBoundaryTime = offsetXToTime(curBoundaryOffsetX);
      updateChapterData &&
        updateChapterData((prevData) => {
          const newData = [...prevData];
          const currentChapter = newData[+index];

          switch (boundary) {
            case 'begin':
              if (
                isOnMagnet &&
                isNearPlayHead(curBoundaryOffsetX) &&
                isValidRange(index, currentTime, currentTime) &&
                currentTime <= currentChapter.endTime
              ) {
                newBoundaryTime =
                  currentTime >= currentChapter.endTime - MIN_DURATION_SEC
                    ? currentChapter.endTime - MIN_DURATION_SEC
                    : currentTime;
              }
              currentChapter.startTime = newBoundaryTime;
              break;

            case 'end':
              if (
                isOnMagnet &&
                isNearPlayHead(curBoundaryOffsetX) &&
                isValidRange(index, currentTime, currentTime) &&
                currentChapter.startTime <= currentTime
              ) {
                newBoundaryTime =
                  currentTime <= currentChapter.startTime + MIN_DURATION_SEC
                    ? currentChapter.startTime + MIN_DURATION_SEC
                    : currentTime;
              }

              currentChapter.endTime = newBoundaryTime;
              break;
            default:
          }

          return newData;
        });

      setSnapPosition({ type: boundary, x: timeToOffsetX(newBoundaryTime) });
    },
    [
      connSeekbar,
      offsetXToTime,
      updateChapterData,
      timeToOffsetX,
      PlayheadDragEventHandler,
      isOnMagnet,
      isNearPlayHead,
      isValidRange,
      currentTime,
    ],
  );

  const ChapterRangeDragEventHandler = useCallback(
    (e: DraggableEvent, dragData: DraggableData) => {
      const { x: chapterStartOffsetX, node } = dragData;
      const { clientWidth: chapterWidth } = node;
      const { index }: DOMStringMap = node.dataset;
      if (!index) return;

      //Drag to begin frame for check the frame
      if (connSeekbar) {
        PlayheadDragEventHandler(e, dragData);
      }

      switch (e.type) {
        case 'mousemove':
          setIsChapterClick(true);
          setDragChapterIdx(+index);
          break;
        case 'mouseup':
          setIsChapterClick(false);
          setDragChapterIdx(null);
          setSnapPosition({ type: '', x: -1 });
          return;
        default:
      }

      const chapterEndOffsetX = chapterStartOffsetX + chapterWidth;
      let newStartTime = offsetXToTime(chapterStartOffsetX);

      updateChapterData &&
        updateChapterData((prevData) => {
          const newData = [...prevData];
          const currentChapter = newData[+index];
          const chapterDuration = currentChapter.endTime - currentChapter.startTime;
          const boundLeftLimitTime = calcBeginBoundTimeLimit(chapterData, currentChapter);
          const boundRightLimitTime =
            calcEndBoundTimeLimit(chapterData, currentChapter) ?? duration;

          if (
            !connSeekbar &&
            isOnMagnet &&
            isNearPlayHead(chapterStartOffsetX) &&
            isValidRange(index, currentTime, currentTime + chapterDuration)
          ) {
            newStartTime = boundLeftLimitTime >= currentTime ? boundLeftLimitTime : currentTime;
          } else if (
            !connSeekbar &&
            isOnMagnet &&
            isNearPlayHead(chapterEndOffsetX) &&
            isValidRange(index, currentTime - chapterDuration, currentTime)
          ) {
            newStartTime = boundRightLimitTime <= currentTime ? boundRightLimitTime : currentTime;
            newStartTime = newStartTime - chapterDuration;
          }

          currentChapter.startTime = newStartTime;
          currentChapter.endTime = newStartTime + chapterDuration;

          return newData;
        });

      setSnapPosition({ type: 'range', x: timeToOffsetX(newStartTime) });
    },
    [
      connSeekbar,
      offsetXToTime,
      updateChapterData,
      timeToOffsetX,
      PlayheadDragEventHandler,
      chapterData,
      duration,
      isOnMagnet,
      isNearPlayHead,
      isValidRange,
      currentTime,
    ],
  );

  const onConnSwitchHandler = () => {
    if (!connSeekbar) {
      setOnMagnet(true);
    }
    setConnSeekbar(!connSeekbar);
  };

  const timeInputChangeHandler = (timeValue: string | null) => {
    if (!timeValue) return;

    let newDuration = timeValue.indexOf(':') < 0 ? parseFloat(timeValue) : timeValue;

    if (typeof newDuration === 'string') {
      newDuration = formatTimeToDigit(newDuration, transcodingFrameRate);
    }

    if (videoStore.playerRef) {
      videoStore.playerRef.currentTime(newDuration);
    }
  };

  return (
    <TimelineBlock type={type} isOnMagnet={isOnMagnet} connSeekbar={connSeekbar}>
      {type !== 'coords' && (
        <div className="timeline-info">
          <div className="timestamp">
            {!!duration && (
              <TimeInput
                type="timeline"
                changeEvent={(timeValue) => {
                  timeInputChangeHandler(timeValue);
                }}
                defaultValue={
                  getTimeFormat(duration) === 'h:mm:ss:ff'
                    ? '0:00:00:00'
                    : getTimeFormat(duration) === 'mm:ss:ff'
                    ? '00:00:00'
                    : '00:00'
                }
                duration={duration}
                transcodingFrameRate={transcodingFrameRate}
                value={currentTime}
                timeFormat={getTimeFormat(duration)}
              />
            )}
          </div>
          {editable && (
            <div className="action-area">
              <div className="left-wrap">
                <div className="switch-conn-seekbar">
                  <label>
                    <span>{t('video.editor.chapter.switch.label')}</span>
                    <span onClick={() => onConnSwitchHandler()}>
                      <Switch
                        // ref={connSwitch}
                        checked={connSeekbar}
                      />
                    </span>
                  </label>
                </div>
                <Tooltip
                  title={t('timeline.icon.tooltip.snap')}
                  placement="bottom"
                  align={{ offset: [0, -6] }}
                  overlayStyle={{
                    fontSize: '10px',
                    display: connSeekbar ? 'none' : '',
                  }}
                  trigger={'hover'}
                >
                  <button
                    className="btn-magnet"
                    onClick={() => {
                      if (!connSeekbar) setOnMagnet(!isOnMagnet);
                    }}
                    disabled={connSeekbar}
                  >
                    <i className="icon">
                      <IconMagnetDis width="18" height="18" />
                    </i>
                  </button>
                </Tooltip>
              </div>
              {/* <div className="zoom-wrap">
              <span className="icon" aria-hidden="true">
                <Icon component={IconZoomOut} />
              </span>
              <div className="zoom-bar">
                <div className="step-circle selected-zoom-step"></div>
                <div className="step-circle"></div>
                <div className="step-circle"></div>
                <div className="step-circle"></div>
              </div>
              <span className="icon" aria-hidden="true">
                <Icon component={IconZoomIn} />
              </span>
            </div> */}
            </div>
          )}
        </div>
      )}

      <div className="timeline-box">
        <i className="timeline-icon">
          {type === 'chapter' ? (
            <Tooltip
              title={t('timeline.icon.tooltip.video')}
              placement="bottom"
              align={{ offset: [0, -6] }}
              overlayStyle={{ fontSize: '10px' }}
            >
              <IconVideo />
            </Tooltip>
          ) : (
            <Tooltip
              title={t('timeline.icon.tooltip.chapter')}
              placement="bottom"
              align={{ offset: [0, -6] }}
              overlayStyle={{ fontSize: '10px' }}
            >
              <IconLibrary />
            </Tooltip>
          )}
        </i>

        <div className="timeline-box-inner" ref={timelineRootElement}>
          <div className="timeline-header">
            <span className="time-point start">00:00:00</span>
            <span className="time-point end">
              {formatDigitToHHmmss(duration, true, true, true)}
            </span>
          </div>
          {isDOMLoaded && duration > 0
            ? [
                <div
                  className="timeline-playhead-background"
                  onMouseEnter={PlayheadBackgroundEnterEventHandler}
                  onMouseDown={PlayheadBackgroundDownEventHandler}
                  onMouseMove={PlayheadBackgroundMoveEventHandler}
                  onMouseUp={PlayheadBackgroundUpEventHandler}
                  onMouseLeave={PlayheadBackgroundLeaveEventHandler}
                  data-seeking={isPlayheadHoverMouseDown ? 'true' : 'false'}
                  aria-hidden="true"
                  key="playhead-background"
                />,
                <div
                  className="timeline-playhead-hover"
                  data-hover={!isPlayheadHoverMouseDown && isPlayheadHoverActive ? 'true' : 'false'}
                  style={{
                    transform: `translateX(${playheadHoverPosX}px)`,
                  }}
                  key="playhead-hover"
                />,
                <Draggable
                  bounds={type === 'coords' ? playheadBounds : 'parent'}
                  handle=".inner-handle"
                  axis="x"
                  position={{ x: timelineDimensionX * currentProgress, y: 0 }}
                  scale={1}
                  onStart={PlayheadStartEventHandler}
                  onDrag={PlayheadDragEventHandler}
                  onStop={PlayheadStopEventHandler}
                  key="playhead"
                >
                  <div
                    className="timeline-playhead"
                    data-active={isPlayheadHoverMouseDown ? 'false' : 'true'}
                  >
                    <div className="inner-handle" aria-label="플레이헤드" />
                  </div>
                </Draggable>,

                <div className="chapter-timeline" key="chapter-timeline">
                  {chapterData.map((chapter, index) => {
                    const startPosX = timeToOffsetX(chapter.startTime);
                    const endPosX = timeToOffsetX(chapter.endTime);
                    const boundLeftXLimit = timeToOffsetX(
                      calcBeginBoundTimeLimit(chapterData, chapter),
                    );
                    const boundRightXLimit = timeToOffsetX(
                      calcEndBoundTimeLimit(chapterData, chapter) ?? duration,
                    );

                    return (
                      <div className="chapter-area" key={chapter.chapterIdx}>
                        {dragChapterIdx === chapter.chapterIdx && snapPosition.type === 'range' && (
                          <div
                            className="chapter-range"
                            data-index={index}
                            style={{ width: endPosX - startPosX, left: snapPosition.x }}
                            aria-label={`${chapter.label} 범위`}
                            aria-disabled={!editable ? 'true' : 'false'}
                            aria-selected={
                              type === 'coords'
                                ? currentChapterIdx === chapter.chapterIdx
                                  ? 'true'
                                  : 'false'
                                : undefined
                            }
                            onMouseOver={() =>
                              onChapterMouseOver({ idx: chapter.chapterIdx, from: 'timeline' })
                            }
                            onMouseLeave={() => {
                              onChapterMouseOver({ idx: -1, from: 'timeline' });
                            }}
                            onClick={() => {
                              if (onChapterClick)
                                onChapterClick({ idx: chapter.chapterIdx, from: 'timeline' });
                            }}
                          >
                            <div
                              className="hover-chapter-tooltip"
                              aria-hidden={
                                !(
                                  ((hoveredChapter.idx < 0 &&
                                    clickedChapter &&
                                    clickedChapter.idx === chapter.chapterIdx) ||
                                    hoveredChapter.idx === chapter.chapterIdx) &&
                                  !isChapterClick
                                )
                              }
                            >
                              <strong className="chapter-tooltip-label">{chapter.label}</strong>
                              {clickedChapter && clickedChapter.idx === chapter.chapterIdx && (
                                <button
                                  className="btn-unfix"
                                  onClick={(e) => {
                                    onChapterClick!({ idx: -1, from: 'timeline' });
                                    // e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <IconVector width="15" height="15" />
                                </button>
                              )}

                              <dl className="chapter-tooltip-content">
                                <div className="chapter-tooltip-time">
                                  <dt>{t('videos.action.edit.chapter.box.start')}</dt>
                                  <dd>
                                    {formatDigitToHHmmss(
                                      chapter.startTime,
                                      true,
                                      chapter.startTime >= 3600,
                                      true,
                                    )}
                                  </dd>
                                </div>
                                <div className="chapter-tooltip-time">
                                  <dt>{t('videos.action.edit.chapter.box.end')}</dt>
                                  <dd>
                                    {formatDigitToHHmmss(
                                      chapter.endTime,
                                      true,
                                      chapter.endTime >= 3600,
                                      true,
                                    )}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                        )}
                        {/* on Dragging Chapter area */}
                        <Draggable
                          disabled={!editable}
                          bounds={{
                            top: 0,
                            right: boundRightXLimit - (endPosX - startPosX),
                            bottom: 0,
                            left: boundLeftXLimit,
                          }}
                          axis="x"
                          position={{
                            x: startPosX,
                            y: 0,
                          }}
                          scale={1}
                          onDrag={ChapterRangeDragEventHandler}
                          onStop={ChapterRangeDragEventHandler}
                        >
                          <div
                            className="chapter-range"
                            data-index={index}
                            style={{
                              width: endPosX - startPosX,
                              visibility:
                                dragChapterIdx === chapter.chapterIdx &&
                                snapPosition.type === 'range'
                                  ? 'hidden'
                                  : 'visible',
                            }}
                            aria-label={`${chapter.label} 범위`}
                            aria-disabled={!editable ? 'true' : 'false'}
                            aria-selected={
                              type === 'coords'
                                ? currentChapterIdx === chapter.chapterIdx
                                  ? 'true'
                                  : 'false'
                                : undefined
                            }
                            onMouseOver={() =>
                              onChapterMouseOver({ idx: chapter.chapterIdx, from: 'timeline' })
                            }
                            onMouseLeave={() => {
                              onChapterMouseOver({ idx: -1, from: 'timeline' });
                            }}
                            onClick={() => {
                              if (onChapterClick)
                                onChapterClick({ idx: chapter.chapterIdx, from: 'timeline' });
                            }}
                          >
                            <div
                              className="hover-chapter-tooltip"
                              aria-hidden={
                                !(
                                  ((hoveredChapter.idx < 0 &&
                                    clickedChapter &&
                                    clickedChapter.idx === chapter.chapterIdx) ||
                                    hoveredChapter.idx === chapter.chapterIdx) &&
                                  !isChapterClick
                                )
                              }
                            >
                              <strong className="chapter-tooltip-label">{chapter.label}</strong>
                              {clickedChapter && clickedChapter.idx === chapter.chapterIdx && (
                                <button
                                  className="btn-unfix"
                                  onClick={(e) => {
                                    onChapterClick!({ idx: -1, from: 'timeline' });
                                    // e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <IconVector width="15" height="15" />
                                </button>
                              )}

                              <dl className="chapter-tooltip-content">
                                <div className="chapter-tooltip-time">
                                  <dt>{t('videos.action.edit.chapter.box.start')}</dt>
                                  <dd>
                                    {formatDigitToHHmmss(
                                      chapter.startTime,
                                      true,
                                      chapter.startTime >= 3600,
                                      true,
                                    )}
                                  </dd>
                                </div>
                                <div className="chapter-tooltip-time">
                                  <dt>{t('videos.action.edit.chapter.box.end')}</dt>
                                  <dd>
                                    {formatDigitToHHmmss(
                                      chapter.endTime,
                                      true,
                                      chapter.endTime >= 3600,
                                      true,
                                    )}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                        </Draggable>
                        {/* on Dragging left Border */}
                        {dragChapterIdx === chapter.chapterIdx && snapPosition.type === 'begin' && (
                          <div
                            className="chapter-boundary chapter-boundary-begin"
                            data-index={index}
                            data-boundary="begin"
                            aria-label={`${chapter.label} 시작 마커`}
                            aria-disabled={!editable ? 'true' : 'false'}
                            aria-selected={
                              type === 'coords'
                                ? currentChapterIdx === chapter.chapterIdx
                                  ? 'true'
                                  : 'false'
                                : undefined
                            }
                            style={{
                              left: snapPosition.x,
                            }}
                          >
                            <div className="inner-handle" />
                          </div>
                        )}
                        {/* after Dragging left Border*/}
                        <Draggable
                          disabled={!editable}
                          bounds={{
                            top: 0,
                            right: endPosX - timeToOffsetX(MIN_DURATION_SEC),
                            bottom: 0,
                            left: boundLeftXLimit,
                          }}
                          handle=".inner-handle"
                          axis="x"
                          position={{ x: startPosX, y: 0 }}
                          scale={1}
                          onDrag={ChapterBoundaryDragEventHandler}
                          onStop={ChapterBoundaryDragEventHandler}
                        >
                          <div
                            className="chapter-boundary chapter-boundary-begin"
                            data-index={index}
                            data-boundary="begin"
                            aria-label={`${chapter.label} 시작 마커`}
                            aria-disabled={!editable ? 'true' : 'false'}
                            aria-selected={
                              type === 'coords'
                                ? currentChapterIdx === chapter.chapterIdx
                                  ? 'true'
                                  : 'false'
                                : undefined
                            }
                            style={{
                              visibility:
                                dragChapterIdx === chapter.chapterIdx &&
                                snapPosition.type === 'begin'
                                  ? 'hidden'
                                  : 'visible',
                            }}
                          >
                            <div className="inner-handle" />
                          </div>
                        </Draggable>
                        {/* on Dragging right Border */}
                        {dragChapterIdx === chapter.chapterIdx && snapPosition.type === 'end' && (
                          <div
                            className="chapter-boundary chapter-boundary-end"
                            data-index={index}
                            data-boundary="end"
                            aria-label={`${chapter.label} 종료 마커`}
                            aria-disabled={!editable ? 'true' : 'false'}
                            aria-selected={
                              type === 'coords'
                                ? currentChapterIdx === chapter.chapterIdx
                                  ? 'true'
                                  : 'false'
                                : undefined
                            }
                            style={{
                              left: snapPosition.x,
                            }}
                          >
                            <div className="inner-handle" />
                          </div>
                        )}
                        {/* after Dragging right Border*/}
                        <Draggable
                          disabled={!editable}
                          bounds={{
                            top: 0,
                            right: boundRightXLimit,
                            bottom: 0,
                            left: startPosX + timeToOffsetX(MIN_DURATION_SEC),
                          }}
                          handle=".inner-handle"
                          axis="x"
                          position={{ x: endPosX, y: 0 }}
                          scale={1}
                          onDrag={ChapterBoundaryDragEventHandler}
                          onStop={ChapterBoundaryDragEventHandler}
                        >
                          <div
                            className="chapter-boundary chapter-boundary-end"
                            data-index={index}
                            data-boundary="end"
                            aria-label={`${chapter.label} 종료 마커`}
                            aria-disabled={!editable ? 'true' : 'false'}
                            aria-selected={
                              type === 'coords'
                                ? currentChapterIdx === chapter.chapterIdx
                                  ? 'true'
                                  : 'false'
                                : undefined
                            }
                            style={{
                              visibility:
                                dragChapterIdx === chapter.chapterIdx && snapPosition.type === 'end'
                                  ? 'hidden'
                                  : 'visible',
                            }}
                          >
                            <div className="inner-handle" />
                          </div>
                        </Draggable>
                      </div>
                    );
                  })}
                </div>,
              ]
            : null}
        </div>
      </div>
    </TimelineBlock>
  );
};

interface StyledTimelineBlockProps {
  type: 'coords' | 'chapter';
  isOnMagnet: boolean;
  connSeekbar: boolean;
}

const TimelineBlock = styled.div<StyledTimelineBlockProps>`
  display: block;
  margin-top: ${({ type }) => (type === 'coords' ? '40px' : '16px')};

  .timeline-info {
    display: flex;
    align-items: center;
    padding: 0 0 48px;
  }

  .timestamp {
    display: block;
    font-style: normal;
  }

  .timestamp-input {
    display: block;
    width: 100%;
    margin: 0;
    padding: 9px 16px 8px;
    border: 1px solid #4f4f4f;
    border-radius: 3px;
    background-color: transparent;
    box-sizing: border-box;
    text-align: center;
    font-weight: bold;
    font-size: 14px;
    line-height: 21px;
    color: #f2f3f6;
    transition: border-color 200ms ease;

    &:focus {
      outline: none;
      border: 1px solid #508bed;
    }
  }

  .action-area {
    display: flex;
    justify-content: space-between;
    flex: 1;
  }

  .left-wrap {
    display: flex;
  }

  .switch-conn-seekbar {
    margin-left: 30px;

    label {
      font-weight: bold;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 0.01em;
      color: #b6b7b8;

      span {
        margin-right: 9px;
      }
    }

    button {
      top: -1px;
      min-width: 29px;
      width: 29px;
      height: 16px;

      &.ant-switch-checked {
        .ant-switch-handle {
          left: calc(100% - 12px);
        }
      }
      .ant-switch-handle {
        width: 8px;
        height: 8px;
        top: 4px;
        left: 4px;

        &::before {
          background-color: #131313;
        }
      }
    }
  }

  .btn-magnet {
    position: relative;
    top: 4px;
    height: 18px;
    margin-left: 15px;
    cursor: pointer;

    .icon {
      width: 18px;
      height: 18px;

      svg {
        display: block;
        width: 18px;
        height: 18px;
      }
    }

    svg path {
      fill: ${({ connSeekbar, isOnMagnet }) =>
        connSeekbar ? '' : isOnMagnet ? '#8100FF' : '#B6B7B8'};
    }
  }

  .zoom-wrap {
    display: flex;
    align-items: center;
    background-color: black;

    .zoom-bar {
      position: relative;
      width: 136px;
      height: 4px;
      margin-right: 17px;
      margin-left: 17px;
      border-radius: 3px;
      background-color: #424242;

      .step-circle {
        position: absolute;
        width: 4px;
        height: 4px;
        border-radius: 2px;
        background-color: #c4c4c4;

        &:nth-of-type(2) {
          left: calc(100% / 3);
        }

        &:nth-of-type(3) {
          left: calc(100% / 3 * 2);
        }

        &:nth-of-type(4) {
          left: calc(100%);
        }

        &.selected-zoom-step {
          &::before {
            ${scMixinPseudoBlock};
            position: relative;
            width: 12px;
            height: 12px;
            top: -4px;
            left: -4px;
            border-radius: 6px;
            background-color: #fff;
          }
        }
      }
    }
  }

  .icon {
    width: 24px;
    height: 24px;

    svg {
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  .timeline-box {
    position: relative;
    padding: 0 0 0 95px;
    border: 1px solid #4f4f4f;

    &::before {
      ${scMixinPseudoBlock}
      position: absolute;
      height: 100%;
      width: 1px;
      left: 65px;
      background-color: #4f4f4f;
    }
  }

  .timeline-icon {
    position: absolute;
    left: 22px;
    top: 50%;
    transform: translateY(-50%);
  }

  .timeline-header {
    display: flex;
    position: absolute;
    right: 0;
    bottom: calc(100% + 6px);
    left: 0;
    padding: 0 0 4px;

    .time-point {
      display: inline-block;
      padding: 0 6px;
      font-size: 12px;
      line-height: 18px;
      color: #4f4f4f;

      &::before {
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        z-index: 1;
        width: 1px;
        background-color: #4f4f4f;
        content: '';
      }

      &.start {
        &::before {
          left: 0;
        }
      }

      &.end {
        margin-left: auto;

        &::before {
          right: 0;
        }
      }
    }
  }

  .timeline-box-inner {
    position: relative;
    height: 80px;
    margin: 5px;
  }

  .timeline-playhead-background {
    position: absolute;
    top: -26px;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;

    &[data-seeking='true'] {
      cursor: col-resize;
      z-index: 4;
    }
  }

  .timeline-playhead-hover {
    display: inline-block;
    position: absolute;
    top: -18px;
    z-index: 1;
    width: 1px;
    height: 106px;
    background-color: #fff;
    opacity: 0;
    pointer-events: none;
    transition: opacity 100ms ease;

    &[data-hover='true'] {
      opacity: 0.5;
    }
  }

  .timeline-playhead {
    display: inline-block;
    position: absolute;
    top: -18px;
    z-index: 4;

    &[data-active='false'] {
      z-index: 1;
    }

    .inner-handle {
      position: relative;
      width: 1px;
      height: 104px;
      margin: 0 0 0 -3px;
      padding: 0 3px 0 4px;
      cursor: col-resize;

      &::before {
        display: block;
        position: absolute;
        top: 0;
        right: 3px;
        bottom: 0;
        left: 3px;
        background-color: #fff;
        content: '';
      }

      &::after {
        display: block;
        position: absolute;
        left: -3px;
        top: -1px;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 6px;
        border-top-width: 10px;
        border-color: #fff transparent transparent transparent;
        transform: translateX(4%);
        content: '';
      }
    }
  }

  .chapter-timeline {
    position: relative;
    width: 100%;
    height: 80px;
    background-image: url('/assets/player/default-frame.jpg');
    background-repeat: repeat-x;
    background-size: auto 100%;
  }

  .chapter-range {
    display: inline-block;
    position: absolute;
    z-index: 2;
    height: 80px;
    background-color: rgba(129, 0, 255, 0.3);
    text-align: center;
    transition: background-color 100ms ease;

    &[aria-disabled='false'] {
      cursor: col-resize;
    }

    &[aria-disabled='true'] {
      pointer-events: none;
    }

    &[aria-selected='false'] {
      background-color: rgba(79, 79, 79, 0.3);
    }
  }

  .hover-chapter-tooltip {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: calc(-100% - 2px);
    left: 50%;
    z-index: 3;
    min-width: 190px;
    padding: 10px 16px 8px;
    border-radius: 3px;
    background-color: #090909;
    text-align: left;
    color: #fcfcfc;
    visibility: hidden;
    opacity: 0;
    transition: opacity 200ms ease;
    transform: translateX(-50%);
    pointer-events: none;

    &[aria-hidden='false'] {
      visibility: visible;
      opacity: 1;
    }

    .btn-unfix {
      position: absolute;
      width: 16px;
      height: 16px;
      right: 17px;
      cursor: pointer;
      overflow: hidden;
      pointer-events: auto;
    }

    .chapter-tooltip-label {
      display: block;
      font-weight: bold;
      font-size: 12px;
      line-height: 1.25;
    }

    .chapter-tooltip-content {
      margin: 6px 0 0;
    }

    .chapter-tooltip-time {
      margin-top: 3px;
      line-height: 1;

      &:first-of-type {
        margin-top: 0;
      }

      dt,
      dd {
        display: inline-block;
        margin: 0;
        font-size: 12px;
        line-height: 1.25;
      }

      dd {
        margin-left: 20px;
      }
    }

    &::after {
      display: block;
      position: absolute;
      left: 50%;
      bottom: -13px;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 6px;
      border-top-width: 10px;
      border-color: #090909 transparent transparent transparent;
      transform: translateX(4%);
      content: '';
    }
  }

  .chapter-boundary {
    display: inline-block;
    position: absolute;
    z-index: 3;

    &[aria-disabled='false'] {
      .inner-handle {
        cursor: ew-resize;
      }
    }

    &[aria-disabled='true'] {
      pointer-events: none;
    }

    &[aria-selected='false'] .inner-handle::before {
      background-color: #4f4f4f;
    }

    .inner-handle {
      position: relative;
      width: 2px;
      height: 80px;
      padding: 4px;
      transform: translate(-4px);

      &::before {
        display: block;
        position: absolute;
        top: -2px;
        right: 0;
        bottom: -2px;
        left: 0;
        background-color: #8100ff;
        content: '';
      }
    }

    &.chapter-boundary-begin .inner-handle::before {
      right: 2px;
      left: 2px;
    }

    &.chapter-boundary-end .inner-handle::before {
      right: 2px;
      left: 2px;
    }
  }
`;

export default VideoTimeline;
